import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Microphone, Stop, X, CheckCircle, Trash, Sparkle } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * PhotoVoicePunchModal — snap photo(s) + say a caption → one punch item
 * with the photos attached, in one round-trip.
 *
 * Flow: pick project (optional — can be pre-supplied) → attach photos →
 * record voice → tap Send → backend creates the item + uploads photos.
 * Optional roomOverride pre-tags every item created (e.g. from Unit page).
 */
export default function PhotoVoicePunchModal({
  projects = [],
  fixedProjectId = "",
  fixedProjectName = "",
  roomOverride = "",
  onClose,
  onSaved,
}) {
  const [projectId, setProjectId] = useState(fixedProjectId || "");
  const [photos, setPhotos] = useState([]); // [{file, url}]
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [sending, setSending] = useState(false);
  const [savedItem, setSavedItem] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);

  useEffect(() => () => {
    // Revoke object URLs on unmount to avoid memory leaks
    photos.forEach(p => URL.revokeObjectURL(p.url));
  }, [photos]);

  const addFiles = (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    if (photos.length + arr.length > 8) { toast.error("Max 8 photos"); return; }
    setPhotos(prev => [...prev, ...arr.map(f => ({ file: f, url: URL.createObjectURL(f) }))]);
  };
  const removePhoto = (i) => setPhotos(photos.filter((_, idx) => idx !== i));

  const startMic = async () => {
    if (!navigator.mediaDevices) { toast.error("Microphone not supported"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => stream.getTracks().forEach(t => t.stop());
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      setAudioBlob(null);
    } catch (e) { toast.error("Mic permission denied"); }
  };
  const stopMic = async () => {
    const mr = mediaRef.current;
    if (!mr) return;
    mr.stop();
    setRecording(false);
    await new Promise(r => setTimeout(r, 300));
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    setAudioBlob(blob);
  };

  const send = async () => {
    if (!projectId) { toast.error("Pick a project first"); return; }
    if (!photos.length) { toast.error("Attach at least one photo"); return; }
    if (!audioBlob) { toast.error("Record a voice caption first"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("audio", audioBlob, "voice.webm");
      photos.forEach(p => fd.append("files", p.file));
      if (roomOverride) fd.append("room_override", roomOverride);
      const r = await api.post(`/ai/photo-punch/${projectId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSavedItem(r.data?.item || null);
      toast.success(`Punch item added · ${r.data?.photo_count || 0} photo${r.data?.photo_count === 1 ? "" : "s"}`);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const canSend = !!projectId && !!photos.length && !!audioBlob && !sending;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" data-testid="photo-voice-punch-modal">
      <div className="w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[95vh] flex flex-col">
        <div className="p-4 border-b-2 border-slate-200 flex items-center gap-2 sticky top-0 bg-white z-10">
          <Sparkle weight="fill" size={22} className="text-violet-600"/>
          <div className="min-w-0 flex-1">
            <div className="font-display font-black text-lg uppercase tracking-tight">Snap + Say · Punch Item</div>
            {(fixedProjectName || roomOverride) && (
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 truncate">
                {fixedProjectName ? `→ ${fixedProjectName}` : ""}{roomOverride ? ` · Unit ${roomOverride}` : ""}
              </div>
            )}
          </div>
          <button onClick={onClose} data-testid="pvp-close"
            className="h-9 w-9 flex items-center justify-center hover:bg-slate-100 rounded"><X size={20}/></button>
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-5">
          {savedItem ? (
            <div className="text-center py-8" data-testid="pvp-saved">
              <CheckCircle weight="fill" size={44} className="mx-auto text-emerald-600"/>
              <div className="mt-3 font-display font-black text-xl uppercase tracking-tight">Item Added</div>
              <div className="mt-2 text-sm text-slate-600">
                <b>{savedItem.room || "—"}</b> · {savedItem.title}
              </div>
              <button onClick={onClose}
                className="mt-6 h-11 px-6 bg-[#0f172a] text-white font-black uppercase text-[10px] tracking-widest">
                Done
              </button>
            </div>
          ) : (
            <>
              {/* Project picker */}
              {!fixedProjectId && (
                <div data-testid="pvp-project-section">
                  <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Target Project</label>
                  <select value={projectId} onChange={e => setProjectId(e.target.value)}
                    data-testid="pvp-project-picker"
                    className="w-full h-11 px-3 border-2 border-[#0f172a] font-bold text-sm bg-white">
                    <option value="">— pick a project —</option>
                    {projects.map(p => (
                      <option key={p.id || p._id} value={p.id || p._id}>
                        {p.name} · {p.property_address}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Photos */}
              <div data-testid="pvp-photo-section">
                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
                  <Camera size={13}/> Photos ({photos.length}/8)
                </div>
                <div className="flex gap-2 flex-wrap">
                  {photos.map((p, i) => (
                    <div key={i} className="relative">
                      <img src={p.url} alt="" className="h-20 w-20 object-cover border-2 border-[#0f172a]"/>
                      <button onClick={() => removePhoto(i)}
                        data-testid={`pvp-photo-remove-${i}`}
                        className="absolute -top-1.5 -right-1.5 h-5 w-5 bg-red-600 text-white flex items-center justify-center border border-white">
                        <Trash size={10} weight="bold"/>
                      </button>
                    </div>
                  ))}
                  {photos.length < 8 && (
                    <button onClick={() => fileInputRef.current?.click()}
                      data-testid="pvp-photo-add"
                      className="h-20 w-20 border-2 border-dashed border-[#0f172a] flex flex-col items-center justify-center text-slate-500 hover:bg-slate-50 text-[9px] font-black uppercase tracking-widest">
                      <Camera size={18}/><span>Add</span>
                    </button>
                  )}
                  <input type="file" ref={fileInputRef} accept="image/*" multiple className="hidden"
                    onChange={e => { addFiles(e.target.files); e.target.value = ""; }}
                    data-testid="pvp-photo-input"/>
                </div>
              </div>

              {/* Voice */}
              <div data-testid="pvp-voice-section">
                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
                  <Microphone size={13}/> Voice Caption
                </div>
                <div className="flex items-center gap-3">
                  {!recording ? (
                    <button onClick={startMic} disabled={sending} data-testid="pvp-mic-start"
                      className="h-16 w-16 bg-fuchsia-600 text-white rounded-full flex flex-col items-center justify-center hover:opacity-90 disabled:opacity-40 shadow-lg">
                      <Microphone weight="fill" size={22}/>
                      <span className="text-[8px] font-black uppercase tracking-widest mt-0.5">Talk</span>
                    </button>
                  ) : (
                    <button onClick={stopMic} data-testid="pvp-mic-stop"
                      className="h-16 w-16 bg-red-600 text-white rounded-full flex flex-col items-center justify-center animate-pulse shadow-lg">
                      <Stop weight="fill" size={22}/>
                      <span className="text-[8px] font-black uppercase tracking-widest mt-0.5">Stop</span>
                    </button>
                  )}
                  <div className="flex-1 text-xs font-bold text-slate-600" data-testid="pvp-voice-state">
                    {recording ? "Recording…" : audioBlob ? `✓ Caption ready (${Math.round(audioBlob.size / 1024)}KB) — tap mic to re-record` : "Say what's wrong. e.g. \"Loose smoke detector, needs mounting.\""}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {!savedItem && (
          <div className="p-3 border-t-2 border-slate-200 sticky bottom-0 bg-white">
            <button onClick={send} disabled={!canSend} data-testid="pvp-send"
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2">
              <CheckCircle weight="fill" size={14}/>
              {sending ? "Sending…" : "Create Punch Item"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
